import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";

import { IActionList, IActionLessons } from "../../../state";
import ModalConfirm from "../../../molecules/ModalConfirm";
import AchievementRate from "../../../organisms/student/ActionList/AchievementRate";
import VolumeList from "../../../organisms/student/ActionList/VolumeList";
import ActionAnswerForm from "../../../organisms/student/ActionList/ActionAnswerForm";
import { ReactComponent as ActionListIcon } from "../../../assets/images/ActionList/icon.svg";

import styles from "./ActionList.module.scss";

export interface IActionListProps {
  actionList: IActionList;
  handleSubmitRealization: (
    lesson_id: number,
    content: string,
    realization_id: number | null,
  ) => void;
  calling: boolean;
}
const ActionList: React.FC<IActionListProps> = ({
  actionList,
  handleSubmitRealization,
  calling,
}) => {
  const history = useHistory();
  const [activeVolumeId, setActiveVolumeId] = useState<number | null>(null);

  // データ上は回答が存在しても内容が空文字の場合は未回答として扱う
  const isRetrospectiveAnswered = useCallback((lesson: IActionLessons) => {
    const content0 = lesson.answer?.contents?.[0]?.content?.trim();
    const content1 = lesson.answer?.contents?.[1]?.content?.trim();

    return !!content0 || !!content1;
  }, []);

  const { achieved, total } = useMemo(() => {
    const totalAnsweredLessons = actionList.teaching_materials.reduce(
      (count, material) => {
        const lessonsWithAnswerCount =
          material.lessons?.filter(
            lesson =>
              isRetrospectiveAnswered(lesson) ||
              lesson.reflection_realization_reference !== null,
          ).length || 0;
        return count + lessonsWithAnswerCount;
      },
      0,
    );

    const achievedLessonsCount = actionList.teaching_materials.reduce(
      (total, material) =>
        total +
        material.lessons!.filter(
          lesson => lesson.reflection_realization_reference !== null,
        ).length,
      0,
    );

    return { achieved: achievedLessonsCount, total: totalAnsweredLessons };
  }, [actionList, isRetrospectiveAnswered]);

  const volumeList = actionList.teaching_materials;

  const checkAnswerContent = useCallback(
    volume => {
      if (!volume.lessons || volume.lessons.length === 0) {
        return false;
      }

      return volume.lessons.some((lesson: IActionLessons) => {
        return (
          isRetrospectiveAnswered(lesson) ||
          lesson.reflection_realization_reference !== null
        );
      });
    },
    [isRetrospectiveAnswered],
  );

  // 両方の条件を満たす場合にのみ有効（disabled状態でない）と判定する
  const hasAnsweredLessons = useCallback(
    volume =>
      volume.lessons && volume.lessons.length > 0 && checkAnswerContent(volume),
    [checkAnswerContent],
  );

  const areAllVolumesUnanswered = useMemo(() => {
    return volumeList.every(volume => !hasAnsweredLessons(volume));
  }, [volumeList, hasAnsweredLessons]);

  const selectedVolume = useMemo(() => {
    return actionList.teaching_materials.find(
      material => material.id === activeVolumeId,
    );
  }, [actionList, activeVolumeId]);

  useEffect(() => {
    if (volumeList.length > 0) {
      const firstVolumeWithAnsweredLessons = volumeList.find(volume =>
        hasAnsweredLessons(volume),
      );

      if (firstVolumeWithAnsweredLessons && activeVolumeId === null) {
        setActiveVolumeId(firstVolumeWithAnsweredLessons.id);
      }
    }
  }, [volumeList, activeVolumeId, hasAnsweredLessons]);

  const [dirtyActionAnswers, setDirtyActionAnswers] = useState<
    Record<number, boolean>
  >({});
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const handleActionAnswerDirty = useCallback(
    (lessonId: number, isDirty: boolean) => {
      setDirtyActionAnswers(prev => {
        if (prev[lessonId] === isDirty) return prev;

        return {
          ...prev,
          [lessonId]: isDirty,
        };
      });
    },
    [],
  );

  const hasDirtyActionAnswers = Object.values(dirtyActionAnswers).some(Boolean);

  const handleVolumeChange = useCallback(
    (volumeId: number) => {
      if (volumeId === activeVolumeId) return;

      if (hasDirtyActionAnswers) {
        setIsOpenConfirmModal(true);
        pendingActionRef.current = () => {
          setDirtyActionAnswers({});
          setActiveVolumeId(volumeId);
        };
        return;
      }

      setActiveVolumeId(volumeId);
    },
    [activeVolumeId, hasDirtyActionAnswers],
  );

  useEffect(() => {
    const unblock = history.block(location => {
      if (!hasDirtyActionAnswers) return;
      pendingActionRef.current = () => {
        setDirtyActionAnswers({});
        unblock();
        history.push(location.pathname);
      };
      setIsOpenConfirmModal(true);
      return false;
    });

    return () => unblock();
  }, [history, hasDirtyActionAnswers]);

  const handleActionConfirm = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setIsOpenConfirmModal(false);

    if (action) action();
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.sideBar}>
          <AchievementRate achieved={achieved} total={total} />
          <VolumeList
            activeVolumeId={activeVolumeId}
            volumeList={volumeList}
            hasAnsweredLessons={hasAnsweredLessons}
            handleVolumeChange={handleVolumeChange}
          />
        </div>
        {areAllVolumesUnanswered ? (
          <div className={styles.messageContainer}>
            <div className={styles.message}>
              <ActionListIcon className={styles.icon} />
              <p className={styles.text}>
                この画面[行動リスト]には、あなたが授業中に入力した振り返りWORKの回答が表示されます。
                <br />
                授業で学んだ力を使って行動したら、[行動リスト]に記録を残しておきましょう。
              </p>
            </div>
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.inner}>
              <div className={styles.header}>
                <p className={styles.title}>行動リスト</p>
                <p className={styles.desc}>
                  振り返りWORKに入力した内容を実践して、行動の内容と気づいたことや思ったことを記録しよう。
                </p>
              </div>
              <div className={styles.content}>
                {selectedVolume?.lessons?.map(lesson => (
                  <ActionAnswerForm
                    key={lesson.id}
                    lesson={lesson}
                    handleSubmitRealization={handleSubmitRealization}
                    calling={calling}
                    isRetrospectiveAnswered={isRetrospectiveAnswered}
                    handleActionAnswerDirty={handleActionAnswerDirty}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {isOpenConfirmModal && (
        <ModalConfirm
          onClose={() => setIsOpenConfirmModal(false)}
          title="別ページに移動すると、保存していない回答が保存されません。移動しますか？"
          buttons={[
            {
              title: "いいえ",
              action: () => setIsOpenConfirmModal(false),
              type: "None",
            },
            {
              title: "はい",
              action: () => handleActionConfirm(),
              type: "Success",
            },
          ]}
          className={styles.confirmModal}
        />
      )}
    </>
  );
};

export default ActionList;
